import { defineComponent as _defineComponent } from 'vue'
import VsNestedPickerListItem from '@/components/vision/vs-nested-picker/VsNestedPickerListItem'
import { VVirtualScroll } from 'vuetify/lib/components'
import { orderBy } from 'lodash'
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'

interface Props {
  value?: unknown[]
  itemHeight?: number | string
  maxHeight?: number | string | null
  items: unknown[]
  multiple?: boolean
  searchInput?: string
  filter: (arg: unknown) => void
}

interface Data {
  selectedNodeId: number | null
  currentNode: Node
  nodes: Map<number | null, Node>
}


export default /*#__PURE__*/_defineComponent({
  __name: 'VsNestedPicker',
  props: {
    value: { default: null },
    itemHeight: { default: 60 },
    maxHeight: { default: null },
    items: { default: () => [] },
    multiple: { type: Boolean, default: false },
    searchInput: { default: '' },
    filter: { type: Function, default: () => () => true }
  },
  emits: ['input', 'expand', 'collapse'],
  setup(__props: any, { emit }) {

const props: Props = __props as { value: unknown[], itemHeight: number | string, maxHeight: number | string | null, items: unknown[], multiple: boolean, searchInput: string, filter: (arg: unknown) => void }



class Node {
  value: unknown
  // eslint-disable-next-line no-use-before-define
  children: Node[]
  // eslint-disable-next-line no-use-before-define
  parent: Node | null
}

const data: Data = reactive<Data>({
  selectedNodeId: null,
  currentNode: null,
  nodes: {},
} as Data)

const selectedNode = computed(() => {
  if (data.selectedNodeId) {
    return props.items.find((x) => x.id === data.selectedNodeId)
  }

  return null
})

const list = computed(() => {
  const recursiveBoundFilter = (item: Node) => {
    return (
      !(props.multiple && props.value?.includes(item.value.id)) &&
      (props.filter(item.value) ||
        item.children.filter(recursiveBoundFilter).length > 0)
    )
  }

  const recursiveSearch = (result, node: Node) => {
    if (searchFilter(node)) {
      result.push(node)
    }
    return node.children.reduce(recursiveSearch, result)
  }

  let items = data.currentNode?.children.filter(recursiveBoundFilter)
  if (props.searchInput) {
    items = items.reduce(recursiveSearch, [])
  }

  return Object.freeze(
    orderBy(
      items,
      [
        (node) => node.value.order,
        (node) => node.children.length > 0,
        (node) => node.value.title.toLowerCase(),
      ],
      ['asc', 'desc', 'asc']
    )
  )
})

watch(
  () => props.items,
  () => {
    const map = new Map(props.items.map((x: unknown) => [x.id ?? 'empty', []]))
    map.set(null, [])
    for (const item of props.items) {
      if (map.get(item.parentId)) {
        const parent = map.get(item.parentId)
        parent?.push(item)
      }
    }

    const nodes = new Map<number | null, Node>()

    const makeTree = (item, parent): Node => {
      const node = new Node()
      node.value = item
      node.parent = parent
      node.children = map.get(item.id ?? 'empty').map((n) => makeTree(n, node))
      nodes.set(node.value.id, node)
      return node
    }

    const root = map.get(null)

    const rootNode: Node = new Node()
    rootNode.value = null
    rootNode.children = root.map((n) => makeTree(n, null))
    rootNode.parent = null

    nodes.set(null, rootNode)
    data.nodes = nodes
    data.currentNode = rootNode
  },
  { immediate: true }
)

watch(
  () => data.selectedNodeId,
  () => {
    data.currentNode = data.nodes.get(data.selectedNodeId)
  },
  {
    immediate: true,
  }
)

const listRef = ref<VVirtualScroll | null>(null)

onMounted(async () => {
  if (props.value && props.value.length === 1) {
    const [selectionId] = props.value
    const selectedItem = props.items.find((x) => x.id === selectionId)
    data.selectedNodeId = selectedItem?.parentId

    // scroll the selected element into view
    await nextTick()
    const selectedIndex: number = list.value.findIndex(
      (x: Node) => x.value.id === selectionId
    )
    listRef.value.$el.scrollTop = selectedIndex * props.itemHeight
  }
})



const select = (id: number) => {
  emit('input', id)

  if (props.multiple) {
    const node = props.items.find((x) => x.id === id)

    if (node.parentId) {
      nextTick(() => {
        if (list.value.length === 0) {
          collapse()
        }
      })
    }
  }
}

const expand = (id: number) => {
  data.selectedNodeId = id
  emit('expand', id)
}

const collapse = () => {
  data.selectedNodeId = selectedNode.value?.parentId
  emit('collapse', data.selectedNodeId)
}

const searchFilter = (node: Node): boolean => {
  if (node.value.type === 'divider' || node.value.type === 'header') {
    return true
  }
  if (props.multiple && props.value?.includes(node.value.id)) {
    return false
  }
  if (!props.searchInput) {
    return true
  }
  const criteria = (node.value.fullName || node.value.title)
    .replaceAll(' ', '')
    .toLocaleLowerCase()
  const searchVal = props.searchInput.replaceAll(' ', '').toLocaleLowerCase()

  return criteria.indexOf(searchVal) > -1
}

return { __sfc: true,props, Node, data, selectedNode, list, listRef, emit, select, expand, collapse, searchFilter, VsNestedPickerListItem, VVirtualScroll }
}

})